import { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Mail, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const BASE_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * "Send password reset email" confirmation dialog.
 *
 * Triggered from the Account Owners card in Settings. We intentionally
 * DO NOT let the admin set a password directly — that's a privacy /
 * trust issue. Instead we hit /auth/forgot-password which:
 *   1. Generates a single-use token (1 hour TTL)
 *   2. Emails the user a "Reset Password" link via Resend
 *   3. The user clicks the link and sets their own new password
 *
 * Admin never sees the password. This matches what the user explicitly
 * asked for: "super admin doesn't get involved, [user] clicks reset
 * password, receives email, changes it, and moves on."
 */
export default function ResetPasswordDialog({ open, user, onClose }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const handleClose = (o) => {
    if (!o) {
      setBusy(false);
      setDone(false);
      setError(null);
      onClose();
    }
  };

  const sendEmail = async () => {
    if (!user?.email) return;
    setBusy(true);
    setError(null);
    try {
      await axios.post(`${BASE_API}/auth/forgot-password`, { email: user.email });
      setDone(true);
      toast.success(`Reset link sent to ${user.email}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not send the reset email. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[460px]" data-testid="reset-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-fuchsia-50 dark:bg-fuchsia-500/10 flex items-center justify-center">
              <Mail className="w-5 h-5 text-fuchsia-500" />
            </div>
            <div>
              <p>Send password reset email</p>
              <p className="text-xs font-normal text-slate-500 mt-0.5">{user?.email}</p>
            </div>
          </DialogTitle>
          <DialogDescription className="pt-2">
            We'll email <strong>{user?.email || "this user"}</strong> a single-use link.
            They'll click it, set their own new password, and be back in. The link
            expires in 1 hour. <em>You won't see the password — that stays between
            them and the system.</em>
          </DialogDescription>
        </DialogHeader>

        {done && (
          <div className="p-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 flex items-start gap-2" data-testid="reset-password-sent-banner">
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
            <div className="text-sm">
              <p className="font-medium">Reset link sent</p>
              <p className="text-xs opacity-80 mt-0.5">Tell them to check their inbox (and the spam folder, just in case).</p>
            </div>
          </div>
        )}

        {error && !done && (
          <div className="p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <div className="text-sm">
              <p className="font-medium">Couldn't send</p>
              <p className="text-xs opacity-80 mt-0.5">{error}</p>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleClose(false)}
            disabled={busy}
            data-testid="reset-password-cancel-btn"
          >
            {done ? "Close" : "Cancel"}
          </Button>
          {!done && (
            <Button
              type="button"
              onClick={sendEmail}
              disabled={busy || !user?.email}
              className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
              data-testid="reset-password-submit-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              {busy ? "Sending…" : "Send reset email"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
